import {
    app,
    Component,
    ojs,
    each,
    component,
    v,
    state,
    payload,
} from "modular-openscriptjs";
import { empty, clearSearchSuggestion, isMobile } from "../../../helpers";
import { api, dc } from "../../../declarations";
import SearchResultProductView from "../../Views/SearchResultProductView";
import QuickOrderCard from "./QuickOrderCard";
import { dom } from "../../../fotastart";

const h = app("h");

ojs(SearchResultProductView, QuickOrderCard);

export default class SearchResults extends Component {
    pageMap = {
        products: { next: 1, total: 0 },
    };

    // Bumped on every dispatched request. A response is only allowed to
    // touch the DOM if its own sequence number still matches the latest
    // one - so if the user keeps typing and a newer request goes out
    // before an older one resolves, the older (now-stale) response is
    // silently discarded instead of overwriting the newer, correct
    // results whenever it happens to come back.
    _searchSeq = 0;
    timeoutId = null;

    constructor() {
        super();
        this.resetMap();
    }

    resetMap() {
        this.pageMap.products = { next: 1, total: 0 };

        // Also invalidate any request that's still in flight at the moment
        // of the reset, even before a replacement request goes out - so a
        // slow response from before this reset can never land afterward.
        this._searchSeq++;
    }

    $$ojs = {
        routeChanged: () => this.resetMap(),
    };

    $$app = {
        has: {
            searchQuery: (ed, event) => {
                if(!dom.get(`[ojs-uid="${this.id}"]`)) {
                    return;
                }

                clearTimeout(this.timeoutId);

                this.timeoutId = setTimeout(() => {
                    this.resetMap();
                    this.getProducts("c-search-products-results");
                }, 500);
            },
        },
    };

    render(...args) {
        return h.div(
            {
                class: "container pt-3 py-5 mb-5 py-md-5",
            },
            h.h4({ class: "fw-bold mb-4 px-1" }, "Search Results"),
            h.InfiniteScroll({
                child: h.div(
                    {
                        id: "c-search-products-results",
                        class: "d-flex gap-3 gap-md-4 flex-wrap justify-content-around justify-content-md-start pt-2 pb-3",
                    },
                    h.Spinner(),
                ),
                callback: (entries, observer, marker) => {
                    entries.forEach((entry) => {
                        if (!entry.isIntersecting) return;
                        this.populateProducts(
                            "c-search-products-results",
                            marker,
                        );
                    });
                },
            }),
            ...args,
        );
    }

    $_mounted(componentId) {
        let c = component(componentId);
        if (!c) return;

        // Fetch on mount in case query was pre-populated
        c.getProducts("c-search-products-results");
    }

    async getProducts(holderId, spinnerHolder = null) {
        const requestSeq = ++this._searchSeq;

        let page = this.pageMap.products.next;
        let query = dc.searchQuery.value ?? "";

        let spinner = null;
        if (spinnerHolder) {
            spinner = h.Spinner({ parent: spinnerHolder, resetParent: true });
        }

        let qs = {
            page,
            interval: 20,
        };

        if (empty(query)) {
            return;
        }

        qs.query = query;

        let res = await api.get("/products/search/query", qs, { silent: true });

        // A newer request has been dispatched since this one went out -
        // this response is stale, don't let it clobber the newer results.
        if (requestSeq !== this._searchSeq) {
            spinner?.remove();
            return;
        }

        if (res) {
            let products = res.data.products.data;
            let currentPage = res.data.products.current_page;
            let lastPage = res.data.products.last_page;
            this.pageMap.products.total = res.data.products.total ?? 0;

            const holder = dom.id(holderId);

            if (currentPage == 1) {
                dom.empty(holder);
            }

            for (let product of products) {
                if (!dom.get(`[feed-product-id="p-${product.id}"]`, holder)) {
                    h.SearchResultProductView(product, {
                        class: "flex-shrink-0 h-100",
                        style: isMobile() ? "width: 150px;" : "width: 280px;",
                        parent: holder,
                        $feed_product_id: `p-${product.id}`,
                        listeners: {
                            click: () => {
                                app("broker").send(
                                    app("$e").product.needs.display,
                                    payload({ product }),
                                );
                            },
                        },
                    });
                }
            }

            if (empty(products) && currentPage == 1) {
                this._showEmpty(holderId, "No results found for your search.");
            }

            if (!empty(products)) {
                this.pageMap.products.next = currentPage + 1;
            }

            if (currentPage === lastPage) {
                h.div(
                    { parent: holder, class: "w-100 mt-4" },
                    h.QuickOrderCard(),
                );
            }
        }

        spinner?.remove();
        clearSearchSuggestion();
    }

    async populateProducts(holderId, marker) {
        this.getProducts(holderId, marker);
    }

    _showEmpty(holderId, message) {
        const holder = dom.id(holderId);
        if (!holder) return;

        h.div(
            {
                parent: holder,
                resetParent: true,
                class: "text-center py-5 w-100",
                id: "vp-no-search-results",
            },
            h.div(
                {
                    class: "rounded-circle mx-auto d-flex align-items-center justify-content-center mb-3",
                    style: "width: 64px; height: 64px; background: rgba(0,0,0,0.04);",
                },
                h.i({
                    class: "bi bi-search text-secondary",
                    style: "font-size: 1.5rem; opacity: 0.6;",
                }),
            ),
            h.h5({ class: "text-dark fw-bold mb-2" }, "No Results Found"),
            h.p({ class: "text-secondary small mb-0" }, message),
        );
    }
}

ojs(SearchResults);
